import React from "react";
import { V3TimeFilter } from "./performanceChartV3Utils";

export interface V3PeriodFilterRowProps {
  timeFilterOptions: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  availableFilters: string[];
  selectedPeriod: string;
  handleSelectPeriod: (value: string) => void;
  v3IntrinsicAvailability: Record<V3TimeFilter, boolean>;
  v3LoadedCoverage: Record<V3TimeFilter, boolean>;
  v3HasDataByPeriod: Record<V3TimeFilter, boolean>;
  isDarkMode?: boolean;
  actions?: React.ReactNode;
  actionsPlacement?: "inline" | "end";
  compactOnMobile?: boolean;
  appearance?: "default" | "alpha";
}

/**
 * Period chip row for the V3 factors chart. Extracted from
 * PerformanceChartV3Content so the detail-page "Net & Benchmark" view can reuse
 * the exact same control (and the same availability / disabled-state logic).
 */
const V3PeriodFilterRow: React.FC<V3PeriodFilterRowProps> = ({
  timeFilterOptions,
  availableFilters,
  selectedPeriod,
  handleSelectPeriod,
  v3IntrinsicAvailability,
  v3LoadedCoverage,
  v3HasDataByPeriod,
  isDarkMode = true,
  appearance = "default",
  actions,
  actionsPlacement = "inline",
  compactOnMobile = false,
}) => {
  const isAlpha = appearance === "alpha";
  const actionsAtEnd = !!actions && actionsPlacement === "end";
  return (
    <div className={`mt-2 ${compactOnMobile ? "px-2" : "px-1 sm:px-2"} ${actionsAtEnd ? "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 lg:grid-cols-[1fr_auto_1fr]" : ""}`}>
      <div className={`flex flex-wrap items-center justify-center font-medium ${compactOnMobile ? "gap-x-2 gap-y-1.5 text-[10px] leading-4 sm:gap-x-4 sm:gap-y-2 sm:text-xs" : "gap-x-4 gap-y-2 text-xs"} ${actionsAtEnd ? "min-w-0 lg:col-start-2" : ""}`}>
        {timeFilterOptions
          .filter(item => (availableFilters as string[]).includes(item.value))
          .map(item => {
            const isUnavailableV3 =
              ((item.value !== "All" &&
                item.value !== "Since inception" &&
                !v3IntrinsicAvailability[item.value as V3TimeFilter]) ||
                !v3LoadedCoverage[item.value as V3TimeFilter] ||
                !v3HasDataByPeriod[item.value as V3TimeFilter]);

            return (
              <button
                key={item.value}
                onClick={() => handleSelectPeriod(item.value)}
                aria-pressed={selectedPeriod === item.value}
                disabled={isUnavailableV3}
                className={`border ${compactOnMobile ? "min-h-6 px-2 py-0.5 sm:px-2.5 sm:py-1" : "px-2.5 py-1"} transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                  isAlpha ? "rounded-v7-control focus:ring-v7-bronze-dark" : `rounded-full ${isDarkMode ? "focus:ring-slate-300" : "focus:ring-gray-300"}`
                } ${
                  isUnavailableV3
                    ? isAlpha
                      ? "cursor-not-allowed border-v7-ink/15 text-v7-ink/30"
                      : isDarkMode
                      ? "text-slate-600 border-slate-800 cursor-not-allowed"
                      : "text-gray-400 border-gray-200 cursor-not-allowed"
                    : selectedPeriod === item.value
                    ? isAlpha
                      ? "border-v7-ink bg-v7-ink text-v7-white shadow-sm"
                      : isDarkMode
                      ? "bg-slate-200 text-black border-slate-200 shadow-sm"
                      : "bg-gray-900 text-white border-gray-900 shadow-sm"
                    : isAlpha
                      ? "border-transparent text-v7-ink-soft hover:bg-v7-paper hover:text-v7-ink"
                      : isDarkMode
                      ? "text-slate-300 hover:text-white hover:bg-slate-900 border-transparent"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-transparent"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        {!actionsAtEnd && actions}
      </div>
      {actionsAtEnd && <div className="min-w-0 self-end justify-self-end">{actions}</div>}
    </div>
  );
};

export default V3PeriodFilterRow;
